/**
 * ClockTile — single location tile for the World Clock strip.
 *
 * Two-row layout:
 *   Row 1: City name + timezone label
 *   Row 2: Time + weather icon + temp
 *
 * Time updates every minute via parent-provided `now` prop.
 * Weather data passed via props (fetched by WorldClock parent).
 */

import { useMemo } from "react";
import {
  Sun, CloudSun, Cloud, CloudRain, CloudSnow, CloudLightning, Snowflake,
} from "@phosphor-icons/react";

const WMO_META = {
  0:  { Icon: Sun,            label: "Clear" },
  1:  { Icon: CloudSun,       label: "Mostly clear" },
  2:  { Icon: CloudSun,       label: "Partly cloudy" },
  3:  { Icon: Cloud,          label: "Overcast" },
  45: { Icon: Cloud,          label: "Fog" },
  48: { Icon: Cloud,          label: "Rime fog" },
  51: { Icon: CloudRain,      label: "Light drizzle" },
  53: { Icon: CloudRain,      label: "Drizzle" },
  55: { Icon: CloudRain,      label: "Dense drizzle" },
  56: { Icon: CloudRain,      label: "Freezing drizzle" },
  57: { Icon: CloudRain,      label: "Dense freezing drizzle" },
  61: { Icon: CloudRain,      label: "Slight rain" },
  63: { Icon: CloudRain,      label: "Rain" },
  65: { Icon: CloudRain,      label: "Heavy rain" },
  66: { Icon: CloudRain,      label: "Freezing rain" },
  67: { Icon: CloudRain,      label: "Heavy freezing rain" },
  71: { Icon: CloudSnow,      label: "Slight snow" },
  73: { Icon: CloudSnow,      label: "Snow" },
  75: { Icon: CloudSnow,      label: "Heavy snow" },
  77: { Icon: Snowflake,      label: "Snow grains" },
  80: { Icon: CloudRain,      label: "Rain showers" },
  81: { Icon: CloudRain,      label: "Moderate showers" },
  82: { Icon: CloudRain,      label: "Violent showers" },
  85: { Icon: CloudSnow,      label: "Snow showers" },
  86: { Icon: CloudSnow,      label: "Heavy snow showers" },
  95: { Icon: CloudLightning, label: "Thunderstorm" },
  96: { Icon: CloudLightning, label: "Thunderstorm + hail" },
  99: { Icon: CloudLightning, label: "Thunderstorm + heavy hail" },
};

function getWeatherMeta(code) {
  return WMO_META[code] || { Icon: Cloud, label: "Unknown" };
}

export default function ClockTile({ city, timezone, tzLabel, weather, now }) {
  const timeStr = useMemo(() => {
    try {
      return new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }).format(now);
    } catch {
      return "--:--";
    }
  }, [timezone, now]);

  const { Icon: WeatherIcon, label: weatherLabel } = weather
    ? getWeatherMeta(weather.weathercode)
    : { Icon: null, label: null };

  const tempStr = weather?.temperature != null
    ? `${Math.round(weather.temperature)}°`
    : null;

  return (
    <div
      className="flex flex-col items-start px-2.5 py-1.5 min-w-0 hover:bg-neutral-200/50 dark:hover:bg-white/[0.08] transition-colors cursor-default"
      title={weatherLabel || city}
    >
      {/* Row 1: City + TZ label */}
      <div className="flex items-center gap-1 leading-none">
        <span className="text-[10px] font-medium text-neutral-500 dark:text-slate-400 truncate max-w-[5rem]">
          {city}
        </span>
        <span className="text-[8px] font-bold text-neutral-400 dark:text-slate-500 tracking-wide shrink-0">
          {tzLabel}
        </span>
      </div>

      {/* Row 2: Time + weather inline */}
      <div className="flex items-center gap-1.5 mt-0.5">
        <span className="text-[12px] font-semibold text-neutral-800 dark:text-slate-100 whitespace-nowrap tabular-nums leading-none">
          {timeStr}
        </span>
        {weather ? (
          <div className="flex items-center gap-0.5" title={weatherLabel}>
            {WeatherIcon && (
              <WeatherIcon
                size={11}
                weight="regular"
                className="text-neutral-400 dark:text-slate-400 shrink-0"
                aria-label={weatherLabel}
              />
            )}
            {tempStr && (
              <span className="text-[10px] font-medium text-neutral-500 dark:text-slate-400 tabular-nums leading-none">
                {tempStr}
              </span>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-0.5 animate-pulse">
            <span className="w-3 h-2.5 rounded bg-neutral-200 dark:bg-slate-700" />
            <span className="w-5 h-2.5 rounded bg-neutral-200 dark:bg-slate-700" />
          </div>
        )}
      </div>
    </div>
  );
}
